import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Badge } from "@/components/ui/badge";
import { PasswordInput } from "@/components/ui/password-input";
import { 
  CalendarIcon, 
  Clock, 
  Video, 
  UserPlus, 
  X, 
  Mail, 
  Loader2, 
  Copy, 
  Check, 
  Link, 
  Sparkles,
  RefreshCw,
  Share2,
  Lock,
  Eye,
  EyeOff,
  ShieldCheck
} from "lucide-react";
import { format } from "date-fns";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { z } from "zod";
import { sendMeetingInvitation } from "@/services/meetingInvitations";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { generateMeetingCode, getMeetingLink } from "@/lib/meetingUtils";
import { hashPassword, generateStrongPassword } from "@/lib/security";

const emailSchema = z.string().email("Invalid email address");

interface Participant {
  email: string;
  name?: string;
}

export interface Meeting {
  id: string;
  title: string;
  description?: string;
  dateTime: Date;
  durationMinutes?: number;
  isInstant: boolean;
  participants?: Participant[];
  meeting_code?: string;
}

export default function CreateMeeting() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [date, setDate] = useState<Date>();
  const [time, setTime] = useState("09:00");
  const [duration, setDuration] = useState<string>("30");
  const [startNow, setStartNow] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSendingInvites, setIsSendingInvites] = useState(false);
  const [scheduledMeeting, setScheduledMeeting] = useState<Meeting | null>(null);
  const [copied, setCopied] = useState(false);
  const [isActivating, setIsActivating] = useState(false);
  
  // Meeting code state - generated upfront
  const [meetingCode, setMeetingCode] = useState(() => generateMeetingCode());
  
  // Participant invitation state
  const [participants, setParticipants] = useState<Participant[]>([]);
  const [newEmail, setNewEmail] = useState("");
  const [newName, setNewName] = useState("");
  const [emailError, setEmailError] = useState("");

  // Password protection state
  const [passwordEnabled, setPasswordEnabled] = useState(false);
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  // Generate a strong password using centralized utility
  const generatePassword = () => generateStrongPassword(8);

  // Auto-generate password when enabled
  const handlePasswordToggle = (enabled: boolean) => {
    setPasswordEnabled(enabled);
    if (enabled && !password) {
      setPassword(generatePassword());
    }
  };

  const regeneratePassword = () => {
    setPassword(generatePassword());
    toast.success("New password generated");
  };

  const regenerateMeetingCode = () => {
    setMeetingCode(generateMeetingCode());
    toast.success("New meeting code generated");
  };

  const copyMeetingLink = async (code?: string) => {
    const link = getMeetingLink(code || meetingCode);
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      toast.success("Meeting link copied to clipboard!");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Failed to copy link");
    }
  };

  const validateForm = (isScheduling: boolean) => {
    const newErrors: Record<string, string> = {};

    if (!title.trim()) {
      newErrors.title = "Meeting title is required";
    }

    if (isScheduling && !date) {
      newErrors.date = "Date is required for scheduled meetings";
    }

    if (isScheduling && date) {
      const [hours, minutes] = time.split(":").map(Number);
      const selectedDateTime = new Date(date);
      selectedDateTime.setHours(hours, minutes, 0, 0);

      if (selectedDateTime <= new Date()) {
        newErrors.dateTime = "Meeting time must be in the future";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const addParticipant = () => {
    const trimmedEmail = newEmail.trim().toLowerCase();
    
    if (!trimmedEmail) {
      setEmailError("Email is required");
      return;
    }

    const result = emailSchema.safeParse(trimmedEmail);
    if (!result.success) {
      setEmailError("Invalid email address");
      return;
    }

    if (participants.some((p) => p.email === trimmedEmail)) {
      setEmailError("This email is already added");
      return;
    }

    setParticipants([
      ...participants,
      { email: trimmedEmail, name: newName.trim() || undefined },
    ]);
    setNewEmail("");
    setNewName("");
    setEmailError("");
  };

  const removeParticipant = (email: string) => {
    setParticipants(participants.filter((p) => p.email !== email));
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      addParticipant();
    }
  };

  const createMeetingInDb = async (isInstant: boolean) => {
    if (!user) {
      toast.error("You must be logged in to create a meeting");
      return null;
    }

    let meetingDateTime: Date;
    let scheduledEnd: Date | null = null;

    if (isInstant) {
      meetingDateTime = new Date();
    } else {
      if (!date) return null;
      const [hours, minutes] = time.split(":").map(Number);
      meetingDateTime = new Date(date);
      meetingDateTime.setHours(hours, minutes, 0, 0);
    }

    if (duration) {
      scheduledEnd = new Date(meetingDateTime.getTime() + parseInt(duration) * 60 * 1000);
    }

    // Hash password if enabled
    let hashedPassword: string | null = null;
    if (passwordEnabled && password) {
      try {
        hashedPassword = await hashPassword(password);
      } catch (error) {
        console.error("Error hashing password:", error);
        toast.error("Failed to set meeting password");
        return null;
      }
    }

    const { data, error } = await supabase
      .from("meetings")
      .insert({
        title: title.trim(),
        description: description.trim() || null,
        host_id: user.id,
        meeting_code: meetingCode,
        is_active: isInstant,
        scheduled_start: meetingDateTime.toISOString(),
        scheduled_end: scheduledEnd?.toISOString() || null,
        password: hashedPassword,
      })
      .select()
      .single();

    if (error) {
      console.error("Error creating meeting:", error);
      toast.error("Failed to create meeting. Please try again.");
      return null;
    }

    return {
      ...data,
      dateTime: meetingDateTime,
      durationMinutes: duration ? parseInt(duration) : undefined,
      isInstant,
      participants: participants.length > 0 ? participants : undefined,
    };
  };

  const sendInvitationEmails = async (meetingId: string, code: string, meetingTitle: string, meetingDateTime: Date, meetingDescription?: string) => {
    if (participants.length === 0) return;

    const hostName = user?.email?.split("@")[0] || "Meeting Host";

    const invitePromises = participants.map((participant) =>
      sendMeetingInvitation({
        recipientEmail: participant.email,
        recipientName: participant.name,
        meetingTitle,
        meetingCode: code,
        hostName,
        scheduledDate: format(meetingDateTime, "PPP"),
        scheduledTime: format(meetingDateTime, "p"),
        description: meetingDescription,
        hasPassword: passwordEnabled && !!password,
      }).catch((error) => {
        console.error(`Failed to send invite to ${participant.email}:`, error);
        return { error: true, email: participant.email };
      })
    );

    const results = await Promise.all(invitePromises);
    const failed = results.filter((r) => r && typeof r === "object" && "error" in r);

    if (failed.length === 0) {
      toast.success(`Invitations sent to ${participants.length} participant(s)`);
    } else if (failed.length < participants.length) {
      toast.warning(`Some invitations failed to send (${failed.length} of ${participants.length})`);
    } else {
      toast.error("Failed to send invitation emails");
    }
  };

  const handleStartNow = async () => {
    if (!validateForm(false)) return;
    if (!user) {
      toast.error("Please log in to create a meeting");
      navigate("/auth");
      return;
    }

    setIsSendingInvites(true);
    
    const meeting = await createMeetingInDb(true);
    if (!meeting) {
      setIsSendingInvites(false);
      return;
    }
    
    // Send invitation emails
    if (participants.length > 0) {
      await sendInvitationEmails(meeting.id, meeting.meeting_code, meeting.title, meeting.dateTime, meeting.description || undefined);
    }
    
    setIsSendingInvites(false);
    
    toast.success("Meeting created! Joining now...");
    navigate(`/meeting/${meeting.id}`);
  };

  const handleSchedule = async () => {
    if (!validateForm(true)) return;
    if (!user) {
      toast.error("Please log in to schedule a meeting");
      navigate("/auth");
      return;
    }

    setIsSendingInvites(true);
    
    const meeting = await createMeetingInDb(false);
    if (!meeting) {
      setIsSendingInvites(false);
      return;
    }
    
    // Send invitation emails
    if (participants.length > 0) {
      await sendInvitationEmails(meeting.id, meeting.meeting_code, meeting.title, meeting.dateTime, meeting.description || undefined);
    }

    setIsSendingInvites(false);

    // Show success state with copy link
    setScheduledMeeting({
      id: meeting.id,
      title: meeting.title,
      description: meeting.description || undefined,
      dateTime: meeting.dateTime,
      durationMinutes: meeting.durationMinutes,
      isInstant: false,
      participants: meeting.participants,
      meeting_code: meeting.meeting_code,
    });
    
    toast.success(
      `Meeting scheduled for ${format(meeting.dateTime, "PPP 'at' p")}`
    );
  };

  const activateScheduledMeeting = async () => {
    if (!scheduledMeeting) return;

    setIsActivating(true);

    const { error } = await supabase
      .from("meetings")
      .update({
        is_active: true,
        scheduled_start: new Date().toISOString(),
      })
      .eq("id", scheduledMeeting.id);

    setIsActivating(false);

    if (error) {
      console.error("Error activating meeting:", error);
      toast.error("Failed to start meeting. Please try again.");
      return;
    }

    toast.success("Meeting started");
    navigate(`/meeting/${scheduledMeeting.id}`);
  };

  const resetForm = () => {
    setTitle("");
    setDescription("");
    setDate(undefined);
    setTime("09:00");
    setDuration("30");
    setStartNow(false);
    setParticipants([]);
    setScheduledMeeting(null);
    setMeetingCode(generateMeetingCode());
    setPasswordEnabled(false);
    setPassword("");
  };

  return (
    <div className="min-h-screen bg-background relative overflow-hidden flex items-center justify-center p-4 lg:p-8">
      {/* Animated background gradients */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-1/2 -left-1/2 w-full h-full bg-gradient-primary opacity-20 blur-3xl rounded-full animate-pulse" />
        <div className="absolute -bottom-1/2 -right-1/2 w-full h-full bg-gradient-secondary opacity-20 blur-3xl rounded-full animate-pulse" style={{ animationDelay: '1s' }} />
      </div>

      <div className="w-full max-w-2xl mx-auto relative z-10">
        <Card className="w-full shadow-card backdrop-blur-sm bg-card/95 border-border/50">
          <CardHeader className="text-center space-y-4 pb-6">
            <div className="flex justify-center">
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-primary rounded-2xl blur-xl opacity-60 animate-pulse" />
                <div className="relative p-4 bg-gradient-primary rounded-2xl shadow-glow">
                  <Video className="h-10 w-10 text-primary-foreground" />
                </div>
              </div>
            </div>

            <div className="space-y-1">
              <CardTitle className="text-2xl sm:text-3xl font-bold bg-gradient-primary bg-clip-text text-transparent">
                Create Meeting
              </CardTitle>
              <CardDescription className="text-sm sm:text-base text-muted-foreground">
                Set up a new video meeting in seconds
              </CardDescription>
            </div>
          </CardHeader>

          <CardContent className="space-y-6 px-4 sm:px-6">
            {/* Success State after scheduling */}
            {scheduledMeeting ? (
              <div className="space-y-6">
                <div className="text-center space-y-2">
                  <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary/10 mb-4">
                    <Check className="h-8 w-8 text-primary" />
                  </div>
                  <h3 className="text-xl font-semibold">Meeting Scheduled!</h3>
                  <p className="text-muted-foreground">
                    {scheduledMeeting.title} on {format(new Date(scheduledMeeting.dateTime), "PPP 'at' p")}
                  </p>
                </div>

                <div className="p-4 rounded-lg border bg-muted/50 space-y-3">
                  <div className="flex items-center gap-2 text-sm font-medium">
                    <Link className="h-4 w-4" />
                    Meeting Link
                  </div>
                  <div className="flex gap-2">
                    <Input
                      readOnly
                      value={getMeetingLink(scheduledMeeting.meeting_code || '')}
                      className="bg-background font-mono text-sm"
                    />
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => copyMeetingLink(scheduledMeeting.meeting_code)}
                      className="shrink-0"
                      title="Copy link"
                    >
                      {copied ? (
                        <Check className="h-4 w-4 text-primary" />
                      ) : (
                        <Copy className="h-4 w-4" />
                      )}
                    </Button>
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => {
                        const link = getMeetingLink(scheduledMeeting.meeting_code || '');
                        const subject = encodeURIComponent(`Join my meeting: ${scheduledMeeting.title}`);
                        const body = encodeURIComponent(
                          `You're invited to join my meeting!\n\n` +
                          `Meeting: ${scheduledMeeting.title}\n` +
                          `Scheduled: ${format(new Date(scheduledMeeting.dateTime), "PPP 'at' p")}\n` +
                          (scheduledMeeting.description ? `Description: ${scheduledMeeting.description}\n` : '') +
                          `\nJoin here: ${link}`
                        );
                        window.open(`mailto:?subject=${subject}&body=${body}`, '_blank');
                      }}
                      className="shrink-0"
                      title="Share via email"
                    >
                      <Mail className="h-4 w-4" />
                    </Button>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Share this link with participants to join the meeting
                  </p>
                </div>

                <div className="flex flex-col sm:flex-row gap-3">
                  <Button
                    onClick={activateScheduledMeeting}
                    className="w-full sm:flex-1"
                    disabled={isActivating}
                  >
                    {isActivating ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Starting...
                      </>
                    ) : (
                      <>
                        <Video className="h-4 w-4 mr-2" />
                        Start Meeting Now
                      </>
                    )}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={resetForm}
                    className="w-full sm:flex-1"
                  >
                    Create Another Meeting
                  </Button>
                  <Button
                    onClick={() => navigate("/dashboard")}
                    className="w-full sm:flex-1"
                  >
                    Go to Dashboard
                  </Button>
                </div>
              </div>
            ) : (
              <>
                {/* Meeting Code Preview */}
                <div className="p-4 rounded-xl border-2 border-dashed border-primary/30 bg-primary/5 space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Sparkles className="h-4 w-4 text-primary" />
                      <span className="text-sm font-medium text-foreground">Your Meeting Code</span>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={regenerateMeetingCode}
                      className="h-8 px-2 text-muted-foreground hover:text-foreground"
                    >
                      <RefreshCw className="h-4 w-4 mr-1" />
                      New Code
                    </Button>
                  </div>
                  
                  <div className="flex items-center gap-3">
                    <div className="flex-1 bg-background rounded-lg p-3 border">
                      <p className="text-xl sm:text-2xl font-mono font-bold text-center tracking-wider text-foreground">
                        {meetingCode}
                      </p>
                    </div>
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => copyMeetingLink()}
                      className="shrink-0 h-12 w-12"
                      title="Copy meeting link"
                    >
                      {copied ? (
                        <Check className="h-5 w-5 text-primary" />
                      ) : (
                        <Copy className="h-5 w-5" />
                      )}
                    </Button>
                  </div>
                  
                  <p className="text-xs text-muted-foreground text-center">
                    {getMeetingLink(meetingCode)}
                  </p>
                </div>

                {/* Meeting Title */}
                <div className="space-y-2">
                  <Label htmlFor="title" className="text-sm font-medium">
                    Meeting Title <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    id="title"
                    placeholder="Weekly Team Sync"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    className={cn(
                      "h-11",
                      errors.title && "border-destructive focus-visible:ring-destructive"
                    )}
                  />
                  {errors.title && (
                    <p className="text-sm text-destructive">{errors.title}</p>
                  )}
                </div>

                {/* Description */}
                <div className="space-y-2">
                  <Label htmlFor="description" className="text-sm font-medium">
                    Description <span className="text-muted-foreground">(optional)</span>
                  </Label>
                  <Textarea
                    id="description"
                    placeholder="Discuss project updates and next steps..."
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    rows={3}
                    className="resize-none"
                  />
                </div>

                {/* Password Protection Toggle */}
                <div className="space-y-4 p-4 rounded-lg border bg-card">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Lock className="h-4 w-4 text-primary" />
                      <div className="space-y-0.5">
                        <Label htmlFor="password-toggle" className="text-base font-medium cursor-pointer">
                          Password Protection
                        </Label>
                        <p className="text-sm text-muted-foreground">
                          Require a password to join
                        </p>
                      </div>
                    </div>
                    <Switch
                      id="password-toggle"
                      checked={passwordEnabled}
                      onCheckedChange={handlePasswordToggle}
                    />
                  </div>

                  {passwordEnabled && (
                    <div className="space-y-3 pt-2 border-t">
                      <div className="flex items-center gap-2">
                        <div className="relative flex-1">
                          <Input
                            id="meeting-password"
                            type={showPassword ? "text" : "password"}
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            placeholder="Enter meeting password"
                            className="h-11 pr-10 font-mono"
                          />
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="absolute right-0 top-0 h-11 w-11 text-muted-foreground hover:text-foreground"
                            onClick={() => setShowPassword(!showPassword)}
                          >
                            {showPassword ? (
                              <EyeOff className="h-4 w-4" />
                            ) : (
                              <Eye className="h-4 w-4" />
                            )}
                          </Button>
                        </div>
                        <Button
                          type="button"
                          variant="outline"
                          size="icon"
                          onClick={regeneratePassword}
                          className="h-11 w-11 shrink-0"
                          title="Generate new password"
                        >
                          <RefreshCw className="h-4 w-4" />
                        </Button>
                      </div>
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        <ShieldCheck className="h-3.5 w-3.5" />
                        <span>Participants will need this password to join your meeting</span>
                      </div>
                    </div>
                  )}
                </div>

                {/* Start Now Toggle */}
                <div className="flex items-center justify-between p-4 rounded-lg border bg-muted/30 transition-colors hover:bg-muted/50">
                  <div className="space-y-0.5">
                    <Label htmlFor="start-now" className="text-base font-medium cursor-pointer">
                      Start immediately
                    </Label>
                    <p className="text-sm text-muted-foreground">
                      Begin meeting right after creation
                    </p>
                  </div>
                  <Switch
                    id="start-now"
                    checked={startNow}
                    onCheckedChange={setStartNow}
                  />
                </div>

                {/* Scheduling Options - Only shown if not starting now */}
                {!startNow && (
                  <div className="space-y-4 p-4 rounded-lg border bg-card">
                    <h3 className="font-medium flex items-center gap-2 text-foreground">
                      <Clock className="h-4 w-4 text-primary" />
                      Schedule Details
                    </h3>

                    <div className="grid gap-4 sm:grid-cols-2">
                      {/* Date Picker */}
                      <div className="space-y-2">
                        <Label className="text-sm font-medium">
                          Date <span className="text-destructive">*</span>
                        </Label>
                        <Popover>
                          <PopoverTrigger asChild>
                            <Button
                              variant="outline"
                              className={cn(
                                "w-full justify-start text-left font-normal h-11",
                                !date && "text-muted-foreground",
                                errors.date && "border-destructive"
                              )}
                            >
                              <CalendarIcon className="mr-2 h-4 w-4" />
                              {date ? format(date, "PPP") : "Pick a date"}
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-auto p-0" align="start">
                            <Calendar
                              mode="single"
                              selected={date}
                              onSelect={setDate}
                              disabled={(date) => date < new Date(new Date().setHours(0, 0, 0, 0))}
                              initialFocus
                              className="pointer-events-auto"
                            />
                          </PopoverContent>
                        </Popover>
                        {errors.date && (
                          <p className="text-sm text-destructive">{errors.date}</p>
                        )}
                      </div>

                      {/* Time Picker */}
                      <div className="space-y-2">
                        <Label htmlFor="time" className="text-sm font-medium">
                          Time <span className="text-destructive">*</span>
                        </Label>
                        <Input
                          id="time"
                          type="time"
                          value={time}
                          onChange={(e) => setTime(e.target.value)}
                          className="h-11"
                        />
                      </div>
                    </div>

                    {/* Duration */}
                    <div className="space-y-2">
                      <Label htmlFor="duration" className="text-sm font-medium">
                        Duration <span className="text-muted-foreground">(optional)</span>
                      </Label>
                      <Select value={duration} onValueChange={setDuration}>
                        <SelectTrigger id="duration" className="h-11">
                          <SelectValue placeholder="Select duration" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="15">15 minutes</SelectItem>
                          <SelectItem value="30">30 minutes</SelectItem>
                          <SelectItem value="60">1 hour</SelectItem>
                          <SelectItem value="90">1.5 hours</SelectItem>
                          <SelectItem value="120">2 hours</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    {errors.dateTime && (
                      <p className="text-sm text-destructive">{errors.dateTime}</p>
                    )}
                  </div>
                )}

                {/* Invite Participants Section */}
                <div className="space-y-4 p-4 rounded-lg border bg-card">
                  <h3 className="font-medium flex items-center gap-2 text-foreground">
                    <UserPlus className="h-4 w-4 text-primary" />
                    Invite Participants <span className="text-muted-foreground font-normal">(Optional)</span>
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    Add email addresses to send meeting invitations and reminders.
                  </p>

                  <div className="space-y-3">
                    <div className="grid gap-3 sm:grid-cols-2">
                      <div className="space-y-1.5">
                        <Label htmlFor="participant-email" className="text-sm font-medium">
                          Email Address
                        </Label>
                        <Input
                          id="participant-email"
                          type="email"
                          placeholder="colleague@example.com"
                          value={newEmail}
                          onChange={(e) => {
                            setNewEmail(e.target.value);
                            setEmailError("");
                          }}
                          onKeyDown={handleKeyDown}
                          className={cn("h-11", emailError && "border-destructive")}
                        />
                      </div>
                      <div className="space-y-1.5">
                        <Label htmlFor="participant-name" className="text-sm font-medium">
                          Name <span className="text-muted-foreground">(Optional)</span>
                        </Label>
                        <Input
                          id="participant-name"
                          type="text"
                          placeholder="John Doe"
                          value={newName}
                          onChange={(e) => setNewName(e.target.value)}
                          onKeyDown={handleKeyDown}
                          className="h-11"
                        />
                      </div>
                    </div>
                    
                    {emailError && (
                      <p className="text-sm text-destructive">{emailError}</p>
                    )}

                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={addParticipant}
                      className="w-full sm:w-auto"
                    >
                      <UserPlus className="h-4 w-4 mr-2" />
                      Add Participant
                    </Button>
                  </div>

                  {/* Participant List */}
                  {participants.length > 0 && (
                    <div className="space-y-2">
                      <Label className="text-sm text-muted-foreground">
                        {participants.length} participant{participants.length !== 1 ? "s" : ""} will receive invitations:
                      </Label>
                      <div className="flex flex-wrap gap-2">
                        {participants.map((participant) => (
                          <Badge
                            key={participant.email}
                            variant="secondary"
                            className="flex items-center gap-1.5 py-1.5 px-3"
                          >
                            <Mail className="h-3 w-3" />
                            <span className="max-w-[200px] truncate">
                              {participant.name || participant.email}
                            </span>
                            {participant.name && (
                              <span className="text-muted-foreground text-xs">
                                ({participant.email})
                              </span>
                            )}
                            <button
                              type="button"
                              onClick={() => removeParticipant(participant.email)}
                              className="ml-1 hover:text-destructive transition-colors"
                            >
                              <X className="h-3 w-3" />
                            </button>
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* Action Buttons */}
                <div className="flex flex-col sm:flex-row gap-3 pt-2">
                  {startNow ? (
                    <Button 
                      onClick={handleStartNow} 
                      className="w-full sm:flex-1 h-12 text-base font-medium" 
                      size="lg"
                      disabled={isSendingInvites}
                    >
                      {isSendingInvites ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          Creating Meeting...
                        </>
                      ) : (
                        <>
                          <Video className="h-4 w-4 mr-2" />
                          Create & Start Meeting
                        </>
                      )}
                    </Button>
                  ) : (
                    <>
                      <Button
                        variant="outline"
                        onClick={handleStartNow}
                        className="w-full sm:flex-1 h-12"
                        size="lg"
                        disabled={isSendingInvites}
                      >
                        {isSendingInvites ? (
                          <>
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            Creating...
                          </>
                        ) : (
                          "Start Now Instead"
                        )}
                      </Button>
                      <Button 
                        onClick={handleSchedule} 
                        className="w-full sm:flex-1 h-12 text-base font-medium" 
                        size="lg"
                        disabled={isSendingInvites}
                      >
                        {isSendingInvites ? (
                          <>
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            Scheduling...
                          </>
                        ) : (
                          <>
                            <CalendarIcon className="h-4 w-4 mr-2" />
                            Schedule Meeting
                          </>
                        )}
                      </Button>
                    </>
                  )}
                </div>

                <Button
                  variant="ghost"
                  onClick={() => navigate("/dashboard")}
                  className="w-full text-muted-foreground hover:text-foreground"
                >
                  Back to Dashboard
                </Button>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
